import { useCallback, useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import type { HubApi } from '../lib/api.js';
import type { ConnectionStatus } from '../lib/connection.js';
import type { OutputBus } from '../lib/output-bus.js';
import { stringToBase64 } from '../lib/base64.js';
import { terminalReplayText } from '../lib/terminal-replay.js';
import { SessionComposer } from './SessionComposer.js';

interface Props {
  api: HubApi;
  bus: OutputBus;
  machineId: string;
  sessionId: string;
  connectionStatus: ConnectionStatus;
}

const TERMINAL_FALLBACK_TAIL_BYTES = 128 * 1024;

/**
 * Renders one agent session as a live terminal. Streams output from the hub,
 * sends keystrokes back, and keeps the PTY size in sync with the viewport.
 */
export function TerminalView({
  api,
  bus,
  machineId,
  sessionId,
  connectionStatus,
}: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const reloadSnapshotRef = useRef<(() => void) | null>(null);
  const previousConnectionStatusRef = useRef(connectionStatus);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);

  const sendText = useCallback(
    (text: string): void => {
      if (!text) return;
      void api.sendInput(machineId, sessionId, stringToBase64(text));
    },
    [api, machineId, sessionId],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isTouchDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    const term = new Terminal({
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 14,
      cursorBlink: !isTouchDevice,
      convertEol: false,
      smoothScrollDuration: isTouchDevice ? 0 : 120,
      theme: {
        background: '#070b12',
        foreground: '#dfe6f3',
        cursor: '#7dd3fc',
        selectionBackground: '#294261',
      },
    });
    const fit = new FitAddon();
    termRef.current = term;
    term.loadAddon(fit);
    term.open(container);
    fit.fit();
    const uninstallTouchScroll = isTouchDevice
      ? installTouchMomentumScroll(container, term)
      : () => undefined;

    const decoder = new TextDecoder();
    let resizeArmed = false;
    let lastCols = term.cols;
    let lastRows = term.rows;
    let disposed = false;
    let writeFrame: number | undefined;
    let writeInFlight = false;
    let writeBuffer = '';
    let writeCallbacks: Array<() => void> = [];
    const updateJumpToBottom = (): void => {
      if (disposed) return;
      const buffer = term.buffer.active;
      setShowJumpToBottom(buffer.baseY - buffer.viewportY > 1);
    };
    const scheduleWriteFlush = (): void => {
      if (writeFrame !== undefined) return;
      writeFrame = window.requestAnimationFrame(flushWriteBuffer);
    };
    function flushWriteBuffer(): void {
      writeFrame = undefined;
      if (writeInFlight || !writeBuffer) return;
      const text = writeBuffer;
      const callbacks = writeCallbacks;
      writeBuffer = '';
      writeCallbacks = [];
      writeInFlight = true;
      term.write(text, () => {
        writeInFlight = false;
        if (disposed) return;
        for (const callback of callbacks) callback();
        updateJumpToBottom();
        if (writeBuffer) scheduleWriteFlush();
      });
    }
    const writeText = (text: string, callback?: () => void): void => {
      if (!text) {
        callback?.();
        return;
      }
      writeBuffer += text;
      if (callback) writeCallbacks.push(callback);
      scheduleWriteFlush();
    };
    const writeBytes = (bytes: Uint8Array, callback?: () => void): void => {
      writeText(decoder.decode(bytes, { stream: true }), callback);
    };
    const writeSnapshot = (
      bytes: Uint8Array,
      callback?: () => void,
      resetTerminal = false,
    ): void => {
      const resetSequence = resetTerminal ? '\x1b[3J\x1b[2J\x1b[H' : '';
      writeText(resetSequence + terminalReplayText(bytes), callback);
    };
    let sawLiveOutput = false;
    let replayReady = false;
    const pendingLiveOutput: Uint8Array[] = [];
    let snapshotRefreshId = 0;
    let acceptingInput = false;
    let inputArmTimer: number | undefined;
    const armInput = (): void => {
      if (inputArmTimer !== undefined) return;
      inputArmTimer = window.setTimeout(() => {
        if (disposed) return;
        term.focus();
        acceptingInput = true;
      }, 100);
    };
    const finishReplay = (
      refreshId: number,
      followLatest: boolean,
      previousDistanceFromBottom: number,
    ): void => {
      if (disposed || refreshId !== snapshotRefreshId) return;
      replayReady = true;
      if (followLatest) {
        term.scrollToBottom();
      } else {
        term.scrollToLine(Math.max(0, term.buffer.active.baseY - previousDistanceFromBottom));
      }
      for (const bytes of pendingLiveOutput.splice(0)) writeBytes(bytes);
      armInput();
    };
    const unsubscribe = bus.subscribe(sessionId, (bytes) => {
      sawLiveOutput = true;
      if (!replayReady) {
        pendingLiveOutput.push(bytes);
        return;
      }
      writeBytes(bytes);
    });
    const loadSnapshot = async (resetTerminal = false): Promise<void> => {
      const refreshId = ++snapshotRefreshId;
      const previousDistanceFromBottom = term.buffer.active.baseY - term.buffer.active.viewportY;
      const followLatest = !resetTerminal || previousDistanceFromBottom <= 1;
      replayReady = false;
      try {
        fit.fit();
        lastCols = term.cols;
        lastRows = term.rows;
        await api.resize(machineId, sessionId, term.cols, term.rows);
        await new Promise((resolve) => window.setTimeout(resolve, 120));
      } catch {
        // Best effort: a stale-size snapshot is still better than a blank terminal.
      }

      try {
        const bytes = await api.sessionOutput(machineId, sessionId, TERMINAL_FALLBACK_TAIL_BYTES);
        if (disposed || refreshId !== snapshotRefreshId) return;

        // Frames received before the snapshot response are already represented
        // by that snapshot because runner messages are ordered on one socket.
        // Keep only output that arrives while xterm is applying the snapshot.
        pendingLiveOutput.splice(0);
        writeSnapshot(
          bytes,
          () => finishReplay(refreshId, followLatest, previousDistanceFromBottom),
          resetTerminal,
        );
      } catch {
        if (disposed || refreshId !== snapshotRefreshId) return;
        if (!sawLiveOutput) bus.replay(sessionId, writeBytes);
        finishReplay(refreshId, followLatest, previousDistanceFromBottom);
      }
    };
    void loadSnapshot();
    reloadSnapshotRef.current = () => void loadSnapshot(true);

    const dataDisposable = term.onData((text) => {
      if (!acceptingInput) return;
      sendText(text);
    });
    const scrollDisposable = term.onScroll(updateJumpToBottom);

    const syncSize = (): void => {
      fit.fit();
      if (!resizeArmed) {
        lastCols = term.cols;
        lastRows = term.rows;
        return;
      }
      if (term.cols === lastCols && term.rows === lastRows) return;
      lastCols = term.cols;
      lastRows = term.rows;
      void api.resize(machineId, sessionId, term.cols, term.rows);
    };

    const observer = new ResizeObserver(() => syncSize());
    observer.observe(container);
    window.addEventListener('orientationchange', syncSize);
    const resizeArmTimer = window.setTimeout(() => {
      resizeArmed = true;
    }, 500);

    return () => {
      disposed = true;
      if (writeFrame !== undefined) window.cancelAnimationFrame(writeFrame);
      if (inputArmTimer !== undefined) window.clearTimeout(inputArmTimer);
      window.clearTimeout(resizeArmTimer);
      observer.disconnect();
      window.removeEventListener('orientationchange', syncSize);
      uninstallTouchScroll();
      unsubscribe();
      dataDisposable.dispose();
      scrollDisposable.dispose();
      term.dispose();
      if (termRef.current === term) termRef.current = null;
      reloadSnapshotRef.current = null;
      setShowJumpToBottom(false);
    };
  }, [api, bus, machineId, sendText, sessionId]);

  useEffect(() => {
    const previous = previousConnectionStatusRef.current;
    previousConnectionStatusRef.current = connectionStatus;
    if (connectionStatus === 'open' && previous !== 'open') {
      reloadSnapshotRef.current?.();
    }
  }, [connectionStatus]);

  const scrollToBottom = useCallback((): void => {
    termRef.current?.scrollToBottom();
    setShowJumpToBottom(false);
  }, []);

  return (
    <div className="terminal-wrap">
      <div className="terminal-frame">
        <div className="terminal" ref={containerRef} />
        {showJumpToBottom && (
          <button
            aria-label="滚到底部"
            className="scroll-jump terminal-jump"
            type="button"
            onClick={scrollToBottom}
          >
            ↓
          </button>
        )}
      </div>
      <SessionComposer api={api} machineId={machineId} sessionId={sessionId} />
    </div>
  );
}

function installTouchMomentumScroll(container: HTMLElement, term: Terminal): () => void {
  const viewport = container.querySelector<HTMLElement>('.xterm-viewport');
  const scrollArea = container.querySelector<HTMLElement>('.xterm-scroll-area');
  if (!viewport || !scrollArea) return () => undefined;

  const overlay = document.createElement('div');
  overlay.className = 'terminal-touch-scroll';
  const spacer = document.createElement('div');
  spacer.className = 'terminal-touch-scroll-spacer';
  overlay.append(spacer);
  container.append(overlay);

  let mirrorFromViewport = false;
  let syncFrame: number | undefined;
  const syncOverlay = (): void => {
    syncFrame = undefined;
    const scrollHeight = Math.max(
      viewport.scrollHeight,
      scrollArea.offsetHeight,
      viewport.clientHeight,
    );
    spacer.style.height = `${scrollHeight}px`;
    if (Math.abs(overlay.scrollTop - viewport.scrollTop) <= 1) return;
    mirrorFromViewport = true;
    overlay.scrollTop = viewport.scrollTop;
    mirrorFromViewport = false;
  };
  const requestSync = (): void => {
    if (syncFrame !== undefined) return;
    syncFrame = window.requestAnimationFrame(syncOverlay);
  };
  const onOverlayScroll = (): void => {
    if (mirrorFromViewport) return;
    viewport.scrollTop = overlay.scrollTop;
  };
  const onViewportScroll = (): void => requestSync();

  overlay.addEventListener('scroll', onOverlayScroll, { passive: true });
  viewport.addEventListener('scroll', onViewportScroll, { passive: true });
  const scrollDisposable = term.onScroll(requestSync);
  const resizeObserver = new ResizeObserver(requestSync);
  resizeObserver.observe(viewport);
  resizeObserver.observe(scrollArea);
  const mutationObserver = new MutationObserver(requestSync);
  mutationObserver.observe(scrollArea, { attributes: true, attributeFilter: ['style'] });
  requestSync();

  return () => {
    if (syncFrame !== undefined) window.cancelAnimationFrame(syncFrame);
    overlay.removeEventListener('scroll', onOverlayScroll);
    viewport.removeEventListener('scroll', onViewportScroll);
    scrollDisposable.dispose();
    resizeObserver.disconnect();
    mutationObserver.disconnect();
    overlay.remove();
  };
}
